import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Button } from "../../components/ui";
import type { GtmLead, GtmSequenceMessageMode } from "../api/GtmApi";
import GtmSessionBanner from "../components/GtmSessionBanner";
import { useGtmLeadsQuery } from "../hooks/useGtmPlayLeads";
import {
  useGtmEnsureSequenceRuleMutation,
  useGtmSequenceTemplatePatchMutation,
  useGtmSequenceTemplateQuery,
} from "../hooks/useGtmPlays";
import { useGtmSessionQuery } from "../hooks/useGtmSession";
import SequenceFlowCanvas from "./SequenceFlowCanvas";
import SequenceNodeDetailPanel from "./SequenceNodeDetailPanel";
import {
  isSequenceMessageStep,
  parseDefinition,
  toReactFlowGraph,
  type SequenceMessageStep,
} from "./sequenceToFlow";

type Metrics = {
  enrolled: number;
  invited: number;
  accepted: number;
  replies: number;
  meetings: number;
};

function computeMetrics(leads: GtmLead[]): Metrics {
  const active = leads.filter((l) => l.status !== "approve");
  return {
    enrolled: active.length,
    invited: active.filter(
      (l) =>
        l.connected === "no" ||
        ["waiting", "review", "hands", "meeting", "ended"].includes(l.status)
    ).length,
    accepted: active.filter((l) => l.connected === "yes").length,
    replies: active.filter((l) => l.status === "hands" || l.status === "meeting").length,
    meetings: active.filter((l) => l.status === "meeting").length,
  };
}

function countsByStep(leads: GtmLead[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const l of leads) {
    if (l.status === "meeting") counts.meeting = (counts.meeting || 0) + 1;
    else if (l.status === "hands") counts.hands = (counts.hands || 0) + 1;
    else if (l.status === "ended") counts.ended = (counts.ended || 0) + 1;
    else if (l.status === "review") counts.connection = (counts.connection || 0) + 1;
    else if (l.status === "waiting" || l.status === "in_sequence") {
      counts.in_sequence = (counts.in_sequence || 0) + 1;
    }
  }
  return counts;
}

type SelectedNode = {
  nodeId: string;
  stepKey: string;
  label: string;
  subtitle?: string;
  kind: string;
};

type Props = {
  projectId: string;
  playId: string;
  onFilterStep?: (stepKey: string) => void;
  onOpenLead?: (leadId: string) => void;
};

export default function PlaySequencePanel({ projectId, playId, onFilterStep, onOpenLead }: Props) {
  const { t } = useTranslation();
  const [ruleId, setRuleId] = useState<string | null>(null);
  const [ensureError, setEnsureError] = useState<string | null>(null);
  const [selectedNode, setSelectedNode] = useState<SelectedNode | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const templateQuery = useGtmSequenceTemplateQuery(projectId, playId);
  const sessionQuery = useGtmSessionQuery(projectId);
  const leadsQuery = useGtmLeadsQuery(projectId, playId);
  const ensureMutation = useGtmEnsureSequenceRuleMutation(projectId);
  const patchMutation = useGtmSequenceTemplatePatchMutation(projectId, playId);

  const definitionJson = templateQuery.data?.definitionJson || "";
  const session = sessionQuery.data ?? null;
  const leads = leadsQuery.data || [];
  const loading = templateQuery.isLoading || sessionQuery.isLoading || leadsQuery.isLoading;
  const loadError =
    (templateQuery.error as any)?.response?.data?.message ||
    (templateQuery.error as any)?.message ||
    (sessionQuery.error as any)?.response?.data?.message ||
    (sessionQuery.error as any)?.message ||
    (leadsQuery.error as any)?.response?.data?.message ||
    (leadsQuery.error as any)?.message ||
    null;
  const error =
    ensureError ||
    (templateQuery.isError || sessionQuery.isError || leadsQuery.isError
      ? loadError || "Failed to load sequence"
      : null);
  const ensuring = ensureMutation.isPending;

  useEffect(() => {
    let cancelled = false;
    ensureMutation
      .mutateAsync()
      .then((res) => {
        if (!cancelled) setRuleId(res.ruleId || null);
      })
      .catch(() => {
        if (!cancelled) setRuleId(null);
      });
    return () => {
      cancelled = true;
    };
    // Ensure once on mount for this play.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, playId]);

  const definition = useMemo(() => parseDefinition(definitionJson), [definitionJson]);
  const graph = useMemo(
    () => toReactFlowGraph(definitionJson, countsByStep(leads)),
    [definitionJson, leads]
  );
  const metrics = useMemo(() => computeMetrics(leads), [leads]);

  const selectedDefinitionNode = useMemo(() => {
    if (!selectedNode) return null;
    return (
      definition.nodes.find(
        (n) => n.id === selectedNode.nodeId || n.step === selectedNode.stepKey || n.outcome === selectedNode.stepKey
      ) ?? null
    );
  }, [definition.nodes, selectedNode]);

  const editingStep: SequenceMessageStep | null =
    selectedNode && isSequenceMessageStep(selectedNode.stepKey) ? selectedNode.stepKey : null;

  const onEnsure = async () => {
    try {
      setEnsureError(null);
      const res = await ensureMutation.mutateAsync();
      setRuleId(res.ruleId || null);
    } catch (e: any) {
      setEnsureError(e?.response?.data?.message || e?.message || "Could not ensure Automation rule");
    }
  };

  const onRetry = () => {
    setEnsureError(null);
    void templateQuery.refetch();
    void sessionQuery.refetch();
    void leadsQuery.refetch();
  };

  const onSaveMessage = async (next: {
    messageMode: GtmSequenceMessageMode;
    messageBody: string;
  }) => {
    if (!editingStep) return;
    try {
      setSaveError(null);
      await patchMutation.mutateAsync({
        messages: [
          {
            step: editingStep,
            messageMode: next.messageMode,
            messageBody: next.messageBody,
          },
        ],
      });
    } catch (e: any) {
      setSaveError(e?.response?.data?.message || e?.message || "Could not save message settings");
    }
  };

  const automationHref = ruleId
    ? `/workspaces/${projectId}/automations/${ruleId}?key=Automations`
    : `/workspaces/${projectId}/automations?key=Automations`;

  if (loading) {
    return <p className="text-sm text-gray-500">{t("Loading sequence…")}</p>;
  }

  if (error) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-red-600">{error}</p>
        <Button type="button" variant="secondary" size="sm" onClick={onRetry}>
          {t("Retry")}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <GtmSessionBanner projectId={projectId} session={session} />
      <div className="flex flex-wrap items-center gap-3">
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${
            session?.status === "connected"
              ? "bg-emerald-50 text-emerald-700"
              : "bg-amber-50 text-amber-800"
          }`}
        >
          {t("Session")}: {session?.status || "missing"}
        </span>
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${
            session?.sequence === "running"
              ? "bg-blue-50 text-blue-700"
              : "bg-gray-100 text-gray-600"
          }`}
        >
          {t("Sequence")}: {session?.sequence || "paused"}
        </span>
        <Link
          className="text-sm font-medium text-blue-600 hover:underline"
          to={`/workspaces/${projectId}/gtm/leads/settings?key=Leads`}
        >
          {t("Configure")}
        </Link>
        <Link className="text-sm font-medium text-blue-600 hover:underline" to={automationHref}>
          {t("Open Automation rule")}
        </Link>
        {!ruleId ? (
          <Button type="button" variant="secondary" size="sm" isLoading={ensuring} onClick={onEnsure}>
            {t("Ensure hourly rule")}
          </Button>
        ) : null}
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        {(
          [
            ["Enrolled", metrics.enrolled],
            ["Invited", metrics.invited],
            ["Accepted", metrics.accepted],
            ["Replies", metrics.replies],
            ["Meetings", metrics.meetings],
          ] as const
        ).map(([label, value]) => (
          <div key={label} className="rounded-lg border border-gray-100 bg-white px-3 py-2">
            <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{t(label)}</div>
            <div className="text-lg font-semibold text-gray-900">{value}</div>
          </div>
        ))}
      </div>

      <div className={`flex flex-col gap-4 ${selectedNode ? "lg:flex-row" : ""}`}>
        <div className="min-w-0 flex-1">
          <SequenceFlowCanvas
            nodes={graph.nodes}
            edges={graph.edges}
            onNodeClick={(nodeId, stepKey) => {
              const node = graph.nodes.find((n) => n.id === nodeId);
              const data = node?.data;
              const key = stepKey || data?.stepKey || nodeId;
              setSaveError(null);
              setSelectedNode({
                nodeId,
                stepKey: key,
                label: data?.label || key,
                subtitle: data?.subtitle,
                kind: data?.kind || "gtmStep",
              });
            }}
          />
          <p className="mt-2 text-xs text-gray-500">
            {t("Click a node to see people on that step, activity logs, and message settings.")}
          </p>
        </div>
        {selectedNode ? (
          <SequenceNodeDetailPanel
            projectId={projectId}
            playId={playId}
            nodeId={selectedNode.nodeId}
            stepKey={selectedNode.stepKey}
            label={selectedNode.label}
            subtitle={selectedNode.subtitle}
            kind={selectedNode.kind}
            definitionNode={selectedDefinitionNode}
            messageMode={(selectedDefinitionNode?.messageMode as GtmSequenceMessageMode) || "ai"}
            messageBody={selectedDefinitionNode?.messageBody || ""}
            savingMessage={patchMutation.isPending}
            messageError={saveError}
            onClose={() => {
              setSelectedNode(null);
              setSaveError(null);
            }}
            onSaveMessage={editingStep ? onSaveMessage : undefined}
            onOpenLead={(leadId) => {
              onOpenLead?.(leadId);
              onFilterStep?.(selectedNode.stepKey);
            }}
          />
        ) : null}
      </div>
    </div>
  );
}
